import { useEffect, useMemo, useState } from 'react';
import { useGetMonthlyCheckInData } from '../api/useGetMonthlyCheckInData';
import {
  CalendarDateItemType,
  useCalendarDateItem,
} from '../calendar/useCalendarDateItem';
import { useCalendarHeaderItem } from '../calendar/useCalendarHeaderItem';
import {
  CalendarTimeItemType,
  useCalendarTimeItem,
} from '../calendar/useCalendarTimeItem';

interface CalendarItemStateHookProps {
  plantID: number;
  initialDate: Date;
}
export const useCalendarItemState = ({
  initialDate,
  plantID,
}: CalendarItemStateHookProps) => {
  //State Item
  const [calendarDateList, setCalendarDateList] = useState(
    [] as CalendarDateItemType[][],
  );
  const [calendarTimeList, setCalendarTimeList] = useState(
    [] as CalendarTimeItemType[],
  );
  const [monthDate, setMonthDate] = useState(initialDate ?? new Date());
  const [dateTimeItem, setDateTimeItem] = useState(initialDate);
  useEffect(() => {
    setDateTimeItem(initialDate);
  }, [initialDate]);
  const [reserveItemList, setReserveItemList] = useState<
    { [date_block: string]: Date[] }[]
  >([]);

  //Month selection
  const calendarMonth = useMemo(() => {
    const offset = 1;
    const padding = 2;
    const year = monthDate.getFullYear().toString().padStart(padding, '0');
    const month = (monthDate.getMonth() + offset)
      .toString()
      .padStart(padding, '0');
    return `${year}-${month}`;
  }, [monthDate]);
  const { checkInDataList } = useGetMonthlyCheckInData({
    m_plant_id: plantID,
    year_month: calendarMonth,
  });
  useEffect(() => {
    setReserveItemList((prev) =>
      Array.isArray(checkInDataList)
        ? checkInDataList.reduce((list, item) => {
            const [[keyDate, timeList]] = Object.entries(item);
            const date = new Date(keyDate);
            if (date instanceof Date && !isNaN(date.getTime())) {
              list.push({ [keyDate]: timeList });
            }
            return list;
          }, [] as { [date_block: string]: Date[] }[])
        : prev,
    );
  }, [checkInDataList]);

  // Calendar Header Items
  const { selectMonth, calendarHeaderItem } = useCalendarHeaderItem({
    month: initialDate,
  });
  useEffect(() => {
    setMonthDate(selectMonth);
  }, [selectMonth]);

  // Calendar Body Items
  const { dateSelect, dateList } = useCalendarDateItem({
    monthDate,
    reserveItemList,
    reserveDate: dateTimeItem,
  });
  useEffect(() => {
    setCalendarDateList((prev) =>
      JSON.stringify(prev) !== JSON.stringify(dateList) ? dateList : prev,
    );
  }, [dateList]);

  // Calendar Time Items
  const { timeSelect, timeList } = useCalendarTimeItem({
    reserveItemList,
    reserveDate: dateTimeItem,
  });
  useEffect(() => {
    setCalendarTimeList(timeList);
  }, [timeList]);

  //Effect of date and time selection
  useEffect(() => {
    const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
    const update = `${dateSelect}T${
      timeSelect !== '' ? timeSelect : '00:00:00'
    }`;
    const updateText = isoPattern.test(update) ? update : 'invalid';
    const updateDateTime = new Date(updateText);
    setDateTimeItem((prev) => {
      if (updateDateTime instanceof Date && !isNaN(updateDateTime.getTime())) {
        return updateDateTime;
      } else {
        return prev;
      }
    });
  }, [dateSelect, timeSelect, setDateTimeItem]);

  const dateTimeCheck = useMemo(() => {
    return !!(
      !!(dateSelect && dateSelect !== '') &&
      !!(timeSelect && timeSelect !== '') &&
      !!(dateTimeItem instanceof Date && !isNaN(dateTimeItem.getTime()))
    );
  }, [dateSelect, timeSelect, dateTimeItem]);

  return {
    dateTimeItem,
    calendarItem: {
      headerItem: calendarHeaderItem,
      dateList: calendarDateList,
      timeList: calendarTimeList,
    },
    dateTimeCheck,
  };
};
